import { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { motion, AnimatePresence } from "framer-motion";
import { FaPalette, FaChartLine, FaCode } from "react-icons/fa";
import card1 from "../../static/media/card1.webp";
import card2 from "../../static/media/card2.webp";
import card3 from "../../static/media/card3.webp";

const items = [
  {
    title: "MAESTRÍA EN BRANDING & CONTENIDO",
    desc:
      "Creamos marcas de impacto visual inigualable y narrativas que dominan el mercado. Nuestro contenido audiovisual y gráfico es producido por expertos, garantizando un posicionamiento de élite que trasciende la competencia.",
    image: card1,
    Icon: FaPalette,
  },
  {
    title: "INGENIERÍA WEB DE ALTO RENDIMIENTO",
    desc:
      "Implementamos soluciones web robustas con código escrito a medida, optimizando la velocidad, la seguridad y la experiencia del usuario (UX). Desarrollos profesionales que son una base sólida para su inversión digital.",
    image: card3,
    Icon: FaCode,
  },
  {
    title: "ESTRATEGIA DIGITAL CON RETORNO SUPERIOR",
    desc:
      "Lideramos campañas de posicionamiento (SEO/SEM) y activación de IA, con un enfoque en la analítica de negocios (Power BI). Diseñamos rutas claras para maximizar su inversión y asegurar un crecimiento medible y sostenido.",
    image: card2,
    Icon: FaChartLine,
  },
];

function ServiceCard({ title, desc, image, Icon, index = 0, hover = true }) {
  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      viewport={{ once: true }}
      whileHover={hover ? { scale: 1.02 } : undefined}
      style={{ width: "100%" }}
    >
      <Box
        sx={{
          mx: "auto",
          width: "100%",
          maxWidth: 340,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)",
          border: "1px solid rgba(128,0,255,0.35)",
          borderRadius: 3,
          boxShadow: "0 0 24px rgba(128,0,255,0.18)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: { xs: 180, sm: 200, md: 220 },
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={image}
            alt={title}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </Box>

        <Box
          sx={{
            position: "relative",
            px: { xs: 2.5, md: 3 },
            pb: { xs: 3, md: 4 },
            pt: 4,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: 0,
              transform: "translate(-50%, -50%)",
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "rgba(128,0,255,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 22px rgba(128,0,255,0.5)",
              border: "1px solid rgba(128,0,255,0.55)",
            }}
          >
            <Icon size={30} color="#8000FF" />
          </Box>

          <Typography
            sx={{
              fontWeight: 600,
              color: "#8000FF",
              fontSize: { xs: "1.02rem", md: "1.18rem" },
              lineHeight: 1.25,
              textAlign: "center",
              mb: 1.2,
              mt: 2,
              px: { xs: 1, md: 2 },
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              color: "#E6E6E6",
              fontSize: { xs: ".92rem", md: "1rem" },
              lineHeight: 1.65,
              textAlign: "center",
              px: { xs: 1, md: 1.5 },
            }}
          >
            {desc}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function ServicesSection() {
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  };

  const activeItem = items[activeIndex];

  return (
    <Box
      id="servicios"
      sx={{
        position: "relative",
        backgroundColor: "#000",
        color: "#fff",
        py: { xs: 3, md: 5 },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(128,0,255,0.25) 0%, rgba(0,0,0,0.9) 100%)",
          zIndex: 1,
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: "1200px",
          mx: "auto",
          px: { xs: 2, sm: 3, md: 6 },
        }}
      >
        <Typography
          component={motion.h2}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          sx={{
            textAlign: "center",
            fontWeight: 600,
            color: "#8000FF",
            letterSpacing: 0.4,
            fontSize: { xs: "1.7rem", md: "2.3rem" },
            mb: { xs: 5, md: 7 },
          }}
        >
          Soluciones DobleDx
        </Typography>

        <Box
          sx={{
            display: { xs: "block", md: "none" },
          }}
        >
          <AnimatePresence mode="wait">
            <Box
              key={activeItem.title}
              component={motion.div}
              initial={{ opacity: 0, x: 45 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -45 }}
              transition={{ duration: 0.35 }}
            >
              <ServiceCard
                title={activeItem.title}
                desc={activeItem.desc}
                image={activeItem.image}
                Icon={activeItem.Icon}
                hover={false}
              />
            </Box>
          </AnimatePresence>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Button
              onClick={handlePrev}
              sx={{
                minWidth: 44,
                width: 44,
                height: 44,
                borderRadius: "50%",
                color: "#fff",
                border: "1px solid rgba(128,0,255,0.55)",
                background: "rgba(128,0,255,0.16)",
                boxShadow: "0 0 18px rgba(128,0,255,0.22)",
                fontSize: "1.4rem",
                lineHeight: 1,
                "&:hover": {
                  background: "rgba(128,0,255,0.32)",
                },
              }}
            >
              ‹
            </Button>

            <Box
              sx={{
                display: "flex",
                gap: 0.8,
                alignItems: "center",
              }}
            >
              {items.map((item, index) => (
                <Box
                  key={item.title}
                  onClick={() => setActiveIndex(index)}
                  sx={{
                    width: activeIndex === index ? 22 : 8,
                    height: 8,
                    borderRadius: 99,
                    cursor: "pointer",
                    background:
                      activeIndex === index
                        ? "#8000FF"
                        : "rgba(255,255,255,0.35)",
                    boxShadow:
                      activeIndex === index
                        ? "0 0 14px rgba(128,0,255,0.75)"
                        : "none",
                    transition: "all 0.25s ease",
                  }}
                />
              ))}
            </Box>

            <Button
              onClick={handleNext}
              sx={{
                minWidth: 44,
                width: 44,
                height: 44,
                borderRadius: "50%",
                color: "#fff",
                border: "1px solid rgba(128,0,255,0.55)",
                background: "rgba(128,0,255,0.16)",
                boxShadow: "0 0 18px rgba(128,0,255,0.22)",
                fontSize: "1.4rem",
                lineHeight: 1,
                "&:hover": {
                  background: "rgba(128,0,255,0.32)",
                },
              }}
            >
              ›
            </Button>
          </Box>
        </Box>

        <Grid
          container
          spacing={{ xs: 3, md: 4 }}
          justifyContent="center"
          alignItems="stretch"
          sx={{
            display: { xs: "none", md: "flex" },
          }}
        >
          {items.map(({ title, desc, image, Icon }, i) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={title}
              sx={{ display: "flex" }}
            >
              <ServiceCard
                title={title}
                desc={desc}
                image={image}
                Icon={Icon}
                index={i}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}